/**
 * Cuts a phrase into the **work units** the journey actually practises.
 *
 * ## Why a phrase is not a work unit
 *
 * The analyzer's phrases are musically right and pedagogically unusable.
 * Measured on the catalogue: median 50 notes, 78% above 30, 39% above 60,
 * up to 748. The first phrase of *Mistral gagnant* is 13 bars and 82 notes.
 *
 * The grievance is not that a beginner is frightened by a long screen — it
 * is that **a run of 82 notes buys one repetition of each transition**,
 * taken in the most loaded cognitive state of the journey. Motor
 * consolidation wants 5 to 20 repetitions of a 3-to-7 element chunk. A unit
 * that long does not tire the learner, it fails to train them. Everything
 * below follows from that: the point of a short unit is that it can be
 * played four times in two minutes, not that it looks smaller.
 *
 * ## Cutting inside a phrase moves no phrase boundary
 *
 * Working bars 1-4, then 5-8, then 9-13 never claims the phrase ends at bar
 * 4. The invariant is the other one: **a work unit never straddles two
 * phrases**. Under that constraint the analyzer keeps every boundary it
 * decided, and the dosage lives here.
 *
 * ## Where the cuts can fall, and who decides
 *
 * Always on a bar line: that is where a pianist restarts, and it is the only
 * seam **shared by both hands** — which the both-hands state needs, since it
 * cannot assemble a left hand cut at bar 4 with a right hand cut at bar 3.
 *
 * *Which* bar lines are candidates, and how good each one is, is a musical
 * fact the analyzer publishes (it already scores the arrival at every
 * interior measure line while detecting phrases). This module never derives
 * that. What it does is pick a subset: pure dosage, retunable without
 * re-analysing the catalogue.
 *
 * Hand placement ranges are deliberately *not* the seam, despite being the
 * most musically appealing signal: measured at 1.32 per bar and per hand,
 * they are four ranges to the 20 notes we are aiming for — far too fine —
 * and they differ between hands, so they structurally cannot carry a shared
 * boundary. They are an excellent scorer, not a carrier.
 *
 * Pure TS: no React, no Studio, no DOM.
 */

import type { AnalysisPhraseSeam } from '@/lib/music/analyzer-types';
import type { Phrase, PhraseNote, RunRange } from './types';

// ── Dosage ────────────────────────────────────────────────────────

/**
 * Notes a unit aims for, counted on the **busiest hand**.
 *
 * A beginner hunting for keys in the no-rhythm states advances at roughly
 * one note every 1.5-2 s, so twenty notes is 30-40 s — short enough that
 * four passes fit in two to three minutes. The number of repetitions is
 * what is being bought; the length of the run is only what permits it.
 *
 * On the busiest hand and not on the total, because the left- and
 * right-hand practice states run one hand alone: a 60/6 texture cut "at 20
 * total" hands back 20 left-hand notes per unit in isolated practice, which
 * is the problem again on the only hand that works.
 */
const TARGET_NOTES = 20;

/**
 * Below this a unit holds no gesture worth repeating, and the step's
 * overhead outweighs its content. Same discipline as the two-key floor on
 * the grip: an object smaller than this has to be merged back.
 */
const MIN_NOTES = 8;

/**
 * No cut is ever *chosen* that leaves more than this behind.
 *
 * It bounds what the walk picks, not what comes out: when no candidate
 * fits between the floor and this, there is no defensible cut and the
 * phrase stays whole — long, but honestly so, rather than split at a line
 * the evidence does not support. Measured upstream, that is about 8% of
 * phrases.
 */
const MAX_NOTES = 32;

/**
 * How much the two hands together weigh against the busiest one alone.
 *
 * `1.5` sits where an ordinary two-hand texture — the hands playing over
 * the same bars — keeps being sized by its busiest hand, while a texture
 * that hands the line from one to the other stops hiding half its notes.
 * At `1`, every two-hand unit would be cut on the total and the single-hand
 * states would get units far under the target.
 */
const BOTH_HANDS_DIVISOR = 1.5;

/**
 * Relative worth of each signal a candidate bar line can carry.
 *
 * These are weights on facts, not facts: they are the journey's opinion
 * about what makes a good place for a beginner to restart, and they live
 * here so that retuning them costs nothing upstream.
 */
const WEIGHT = {
  /**
   * Scales the detector's arrival onto the same range as the booleans.
   *
   * Measured upstream, a seam's arrival runs to about 0.21 at the 90th
   * percentile, so this puts the best lines a little under a chord change
   * and well under a repositioning of both hands.
   */
  arrival: 6,
  /** The music turns the page: both hands take a new placement here. */
  bothHandsReplace: 3,
  /**
   * Charged per hand that holds a note across the line. A clean line is
   * preferred, and strongly, but a compromised cut still beats a
   * traversal — which is what a veto handed back.
   */
  handHolds: 2,
  /**
   * Harmonic rhythm — and continuity with what the chord step just showed.
   *
   * A breath (a rest or a long note before the line) has no weight of its
   * own: the analyzer already folds it into `evidence`, and counting it
   * twice would let a silence outrank a repositioning of both hands.
   */
  chordChange: 2,
} as const;

// ── Public types ──────────────────────────────────────────────────

/**
 * A bar line inside a phrase, reduced to what the dosage reads.
 *
 * Built from `AnalysisPhraseSeam` — the analyzer publishes the facts, this
 * side turns them into what it weighs.
 */
export type CutCandidate = {
  readonly tick: number;
  readonly measure: number;
  /**
   * How much the phrase detector saw arriving here, `evidence × hypermeter`.
   *
   * `closure` is deliberately left out of the product. It downgrades a line
   * whose harmony is a pre-dominant, because a *phrase* cannot have arrived
   * there — but a practice unit has no such obligation, and stopping on the
   * IV that leads to bar 9 is a perfectly good place to end a drill. Using
   * the detector's own ranking would apply a penalty built for another
   * question, already applied and invisible.
   */
  readonly arrival: number;
  /** Neither hand keeps a placement across this line. */
  readonly bothHandsReplace: boolean;
  /** A chord boundary falls here. */
  readonly chordChange: boolean;
  /**
   * How many hands hold a note across this line, 0 to 2.
   *
   * Not a veto on its own. Cutting under a sustained note does not break
   * anything: the note starts before the unit, so it is simply not in it,
   * and the run begins on whatever is struck at the line. What is lost is
   * context — a bass held under the passage — which is a blemish, not an
   * impossibility. An earlier version read this as the same hard constraint
   * that governs a hand *placement*, where "the hand cannot leave a key it
   * is still pressing" is a fact about a shape at one instant. A boundary
   * in time is not a shape, and the analogy was over-applied: measured on
   * Mistral gagnant, one sustained vocal line vetoed all eleven candidates
   * of a 13-bar phrase and handed back the traversal untouched.
   *
   * Two hands holding is still a veto — no state can start cleanly there.
   */
  readonly handsHolding: number;
};

/**
 * Read an analyzer seam as a candidate.
 *
 * `chordChange` is not on the seam: chord boundaries are already published
 * in `chords[]`, and the analyzer declined to duplicate them. Composing two
 * published facts is not re-deriving a decision.
 */
export function toCutCandidate(
  seam: AnalysisPhraseSeam,
  chordChange: boolean,
): CutCandidate {
  const hands = seam.hands;

  return {
    tick: seam.tick,
    measure: seam.measure,
    arrival: seam.evidence * seam.hypermeter,
    // Absent hands means no note here carries one, so there is no placement
    // a cut could break — but also nothing attesting the music turns the
    // page, so this stays false rather than becoming a free bonus.
    bothHandsReplace:
      hands !== undefined &&
      !hands.left.keepsPlacement &&
      !hands.right.keepsPlacement,
    handsHolding:
      hands === undefined
        ? 0
        : (hands.left.holdsNote ? 1 : 0) + (hands.right.holdsNote ? 1 : 0),
    chordChange,
  };
}

/** One practised span. This is what the UI calls a phrase. */
export type WorkUnit = {
  readonly startTick: number;
  readonly endTick: number;
  readonly startMeasure: number;
  readonly endMeasure: number;
  /**
   * What this unit costs the player, in notes — the quantity the dosage is
   * aimed at. See `load`: it is the busiest hand, except on a texture that
   * hands the line from one to the other, where the two together weigh
   * more. Not a note count of anything in particular, which is why it is
   * not called one.
   */
  readonly load: number;
};

// ── Build ─────────────────────────────────────────────────────────

/**
 * Cut `phrase` into work units at some subset of `candidates`.
 *
 * Returns a single unit covering the whole phrase when no cut is defensible
 * — a short phrase, or a long one whose every bar line is held over. The
 * caller must not read that as a failure: most phrases are one unit, and
 * the player is never told a subdivision did or did not happen.
 */
export function buildWorkUnits(
  phrase: Phrase,
  candidates: readonly CutCandidate[],
): WorkUnit[] {
  if (load(phrase, phrase.startTick, phrase.endTick) <= MAX_NOTES) {
    return [wholePhrase(phrase)];
  }

  const usable = candidates
    .filter((c) => c.handsHolding < 2)
    .filter((c) => c.tick > phrase.startTick && c.tick < phrase.endTick)
    .slice()
    .sort((a, b) => a.tick - b.tick);

  if (usable.length === 0) {
    return [wholePhrase(phrase)];
  }

  const chosen = chooseCuts(phrase, usable);

  if (chosen.length === 0) {
    return [wholePhrase(phrase)];
  }

  return toUnits(phrase, chosen);
}

/**
 * Greedy walk: from the current unit's start, take the candidate that leaves
 * the unit closest to `TARGET_NOTES` without dropping under `MIN_NOTES`,
 * breaking near-ties on the cut's musical worth.
 *
 * Greedy rather than optimal on purpose. An exact solve would buy a
 * marginally better fit on the last unit of a phrase, at the cost of a
 * search whose result nobody can explain when a cut looks odd. The greedy
 * walk's mistakes are local and readable.
 */
function chooseCuts(
  phrase: Phrase,
  candidates: readonly CutCandidate[],
): CutCandidate[] {
  const chosen: CutCandidate[] = [];
  let from = phrase.startTick;
  let next = 0;

  while (next < candidates.length) {
    const best = bestCut(phrase, from, candidates.slice(next));

    if (best === null) {
      break;
    }

    // The tail left after this cut must itself be worth a unit; when it is
    // not, stop cutting and let the last unit run long rather than emit a
    // stub. `MAX_NOTES` is a target the tail may exceed — a phrase whose
    // remainder cannot be cut is a phrase we have already improved.
    if (load(phrase, best.tick, phrase.endTick) < MIN_NOTES) {
      break;
    }

    chosen.push(best);
    from = best.tick;
    next = candidates.indexOf(best) + 1;
  }

  return chosen;
}

function bestCut(
  phrase: Phrase,
  from: number,
  candidates: readonly CutCandidate[],
): CutCandidate | null {
  let best: CutCandidate | null = null;
  let bestScore = -Infinity;

  for (const candidate of candidates) {
    const size = load(phrase, from, candidate.tick);

    if (size < MIN_NOTES) {
      continue;
    }

    if (size > MAX_NOTES) {
      // Past the ceiling, and candidates only grow from here. Taking this
      // one anyway — which an earlier version did when it was the first to
      // clear the floor — hands back the traversal the module exists to
      // remove: measured, a phrase whose early bar lines are all held over
      // produced a single 40-note unit.
      break;
    }

    const score = cutScore(candidate, size);

    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

/**
 * How good a cut is: how close it leaves the unit to the target, nudged by
 * what the bar line carries musically.
 *
 * The size term dominates — a beautifully-placed cut that leaves 31 notes
 * has not solved the problem the unit exists for. The musical terms decide
 * between the several bar lines that all land near the target, which is the
 * ordinary case in a 4/4 piece.
 */
function cutScore(candidate: CutCandidate, size: number): number {
  const distance = Math.abs(size - TARGET_NOTES) / TARGET_NOTES;
  const musical =
    candidate.arrival * WEIGHT.arrival +
    (candidate.bothHandsReplace ? WEIGHT.bothHandsReplace : 0) +
    (candidate.chordChange ? WEIGHT.chordChange : 0) -
    candidate.handsHolding * WEIGHT.handHolds;

  return musical - distance * 10;
}

// ── Internals ─────────────────────────────────────────────────────

function toUnits(phrase: Phrase, cuts: readonly CutCandidate[]): WorkUnit[] {
  const units: WorkUnit[] = [];
  let startTick = phrase.startTick;
  let startMeasure = phrase.startMeasure;

  for (const cut of cuts) {
    units.push({
      startTick,
      endTick: cut.tick,
      startMeasure,
      endMeasure: cut.measure - 1,
      load: load(phrase, startTick, cut.tick),
    });

    startTick = cut.tick;
    startMeasure = cut.measure;
  }

  units.push({
    startTick,
    endTick: phrase.endTick,
    startMeasure,
    endMeasure: phrase.endMeasure,
    load: load(phrase, startTick, phrase.endTick),
  });

  return units;
}

function wholePhrase(phrase: Phrase): WorkUnit {
  return {
    startTick: phrase.startTick,
    endTick: phrase.endTick,
    startMeasure: phrase.startMeasure,
    endMeasure: phrase.endMeasure,
    load: load(phrase, phrase.startTick, phrase.endTick),
  };
}

/**
 * What a span costs the player, in notes.
 *
 * The busiest hand, because the two single-hand practice states run one
 * hand alone: a 60/6 texture sized on the total hands back 60 left-hand
 * notes in isolated practice, which is the problem again on the only hand
 * that works.
 *
 * But *also* the two hands together, scaled: `max(left, right)` alone lets
 * a sequential texture through untouched — measured, thirty left-hand notes
 * followed by thirty right-hand ones reads as thirty and stays one unit,
 * while the both-hands states play all sixty. `BOTH_HANDS_DIVISOR` is what
 * makes those sixty count as more than thirty without making an ordinary
 * two-hand texture, where the hands overlap, count double.
 */
function load(phrase: Phrase, from: number, to: number): number {
  const left = countIn(phrase.notesByHand.left, from, to);
  const right = countIn(phrase.notesByHand.right, from, to);

  return Math.max(left, right, Math.round((left + right) / BOTH_HANDS_DIVISOR));
}

function countIn(
  notes: readonly PhraseNote[],
  from: number,
  to: number,
): number {
  let n = 0;

  for (const note of notes) {
    if (note.startTick >= from && note.startTick < to) {
      n += 1;
    }
  }

  return n;
}

// ── Junction window ───────────────────────────────────────────────

/**
 * The span the chain step plays: the previous unit and this one, whole, one
 * after the other.
 *
 * It used to play a window around the seam — the last four attacks of the
 * previous unit, the first eight of this one, both edges snapped to bar
 * lines — on the argument that replaying two units whole would rebuild the
 * long run the dosage exists to remove. That argument was borrowed from the
 * wrong step. The dosage bounds what is being *learnt*: a run of eighty
 * notes buys one repetition of each transition, taken in the most loaded
 * state of the journey. By `S5` both units are already fluent, nothing new
 * is being acquired, and the whole point of the step is to weld two chunks
 * into one longer chunk. Here length is the exercise, not the cost.
 *
 * And the window bought that length at a price nothing else in the parcours
 * charges: **it started the player somewhere they had never started**. Every
 * other step opens on a unit boundary, the one place where the hand position
 * is settled and the first key is the one the panel has just been showing.
 * Dropped four attacks before the end of the previous unit, the player had
 * to find an entry point they had never rehearsed, and the step read as hard
 * for a reason that had nothing to do with the seam.
 *
 * The window also overshot its own constants. The head fell back to the
 * whole unit as soon as the unit held eight attacks or fewer — which, at a
 * median of eight groups per single-hand run over PDMX 450, was most of
 * them. It was already playing the current unit whole a good part of the
 * time, while claiming not to, and the panel's two cards (which have always
 * shown each unit's full measure range) were describing a span the step did
 * not play.
 *
 * Both edges are unit boundaries, so both sit on bar lines for free, which
 * the transport requires: it runs `snapToBar` on the tick it is handed, so a
 * range starting mid-bar makes the count-in lead to the bar *before* it and
 * the notes in between land in the run's orphan bucket, against a budget
 * meant for slips.
 */
export function junctionWindow(
  previous: Phrase | null,
  current: Phrase,
): RunRange {
  if (previous === null) {
    return current;
  }

  return {
    // The chain is driven by the current unit's FSM, so its id is the one a
    // debug surface or a captured run should be filed under.
    id: current.id,
    startTick: previous.startTick,
    endTick: current.endTick,
    startMeasure: previous.startMeasure,
    endMeasure: current.endMeasure,
    notesByHand: {
      left: [...previous.notesByHand.left, ...current.notesByHand.left],
      right: [...previous.notesByHand.right, ...current.notesByHand.right],
    },
  };
}
